/**
 * UF, UTM, dólar y euro del día.
 *
 * El contrato se cobra en UF y se factura en pesos, así que el valor del día
 * es un dato de trabajo, no una curiosidad: entre el cierre de un estado de
 * pago y el siguiente la UF se mueve, y esa diferencia son millones.
 *
 * Los publica el Banco Central; se leen de mindicador.cl, que los expone sin
 * clave. Se guardan en el dispositivo: en planta la señal va y viene, y más
 * vale mostrar el valor de ayer diciendo de cuándo es que no mostrar nada.
 */

import { useCallback, useEffect, useState } from 'react';

const FUENTE = 'https://mindicador.cl/api';
const CLAVE = 'xtreme:indicadores:v1';

export interface Indicador {
  valor: number;
  /** Día al que corresponde el valor, no el de la consulta. */
  fecha: string;
}

export interface Indicadores {
  uf: Indicador | null;
  utm: Indicador | null;
  dolar: Indicador | null;
  euro: Indicador | null;
  /** Cuándo se trajeron. Sirve para saber si están frescos. */
  consultadoEn: string;
}

interface RespuestaIndicador {
  valor?: number;
  fecha?: string;
}

function leerGuardados(): Indicadores | null {
  try {
    const s = localStorage.getItem(CLAVE);
    return s ? (JSON.parse(s) as Indicadores) : null;
  } catch {
    return null;
  }
}

function guardar(i: Indicadores) {
  try {
    localStorage.setItem(CLAVE, JSON.stringify(i));
  } catch {
    // Sin espacio: se consultan de nuevo la próxima vez.
  }
}

const deRespuesta = (x: RespuestaIndicador | undefined): Indicador | null =>
  x?.valor != null && x.fecha ? { valor: x.valor, fecha: x.fecha.slice(0, 10) } : null;

/** ¿Lo guardado es de hoy? La UF cambia todos los días. */
function esDeHoy(i: Indicadores | null): boolean {
  return i != null && i.consultadoEn.slice(0, 10) === new Date().toISOString().slice(0, 10);
}

export function useIndicadores() {
  const [indicadores, setIndicadores] = useState<Indicadores | null>(() => leerGuardados());
  const [cargando, setCargando] = useState(false);

  const consultar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await fetch(FUENTE);
      if (!r.ok) return;
      const d = (await r.json()) as Record<string, RespuestaIndicador>;
      const nuevos: Indicadores = {
        uf: deRespuesta(d.uf),
        utm: deRespuesta(d.utm),
        dolar: deRespuesta(d.dolar),
        euro: deRespuesta(d.euro),
        consultadoEn: new Date().toISOString(),
      };
      if (!nuevos.uf) return; // respuesta rara: mejor quedarse con lo anterior
      setIndicadores(nuevos);
      guardar(nuevos);
    } catch {
      // Sin conexión. Se sigue mostrando lo último que se supo.
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    if (esDeHoy(leerGuardados())) return;
    void consultar();
  }, [consultar]);

  return { indicadores, cargando, consultar, alDia: esDeHoy(indicadores) };
}
